import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { applyTheme } from "../utils/theme";
import Sidebar from "../components/common/Sidebar";
import Navbar from "../components/common/Navbar";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import { useApi } from "../hooks/useApi";
import { message } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useLanguageStyles } from "../hooks/useLanguageStyles";

const Account = () => {
  useTranslation(); // reserved for i18n
  const mode = useSelector((state) => state.theme.mode);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { textAlign } = useLanguageStyles();

  // Current user (for display)
  const [currentUser, setCurrentUser] = useState(null);

  // Change password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Change email form
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const { getCurrentUser, changePassword, updateProfile } = useApi();

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await getCurrentUser();
        if (res && res.user) setCurrentUser(res.user);
      } catch (e) {
        console.error("Failed to load current user", e);
      }
    };
    fetchUser();
  }, [getCurrentUser]);

  const validatePassword = (password) => {
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return re.test(password);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    if (!currentPassword.trim()) {
      setPasswordError("Current password is required.");
      return;
    }
    if (!newPassword.trim()) {
      setPasswordError("New password is required.");
      return;
    }
    if (!validatePassword(newPassword)) {
      setPasswordError("New password must be at least 8 characters with uppercase, lowercase, and number.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirm password do not match.");
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await changePassword(currentPassword, newPassword);
      if (res && res.success) {
        message.success("Password updated successfully. Please log in again with your new password.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        // Optional: log out and redirect to login
        localStorage.removeItem("access_token");
        window.location.href = "/login";
      } else {
        setPasswordError(res?.msg || "Failed to update password.");
      }
    } catch (err) {
      setPasswordError(err.message || "Failed to update password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError("");
    if (!newEmail.trim()) {
      setEmailError("New email is required.");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(newEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailLoading(true);
    try {
      const res = await updateProfile({ email: newEmail });
      if (res && res.success) {
        message.success("Email updated successfully. You may need to log in again.");
        setCurrentUser((prev) => (prev ? { ...prev, email: newEmail } : null));
        setNewEmail("");
      } else {
        setEmailError(res?.message || "Failed to update email.");
      }
    } catch (err) {
      setEmailError(err.message || "Failed to update email.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-main">
      <Navbar
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <div className="flex flex-col lg:flex-row">
        <Sidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="flex-1 p-4 lg:p-6 xl:p-8 w-full overflow-x-auto">
          <h1 className={`text-2xl font-bold text-text-primary mb-6 ${textAlign}`}>
            Account settings
          </h1>

          {/* Current account info */}
          {currentUser && (
            <div className="bg-background-card rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-lg font-semibold text-text-primary mb-2">Current account</h2>
              <p className="text-text-secondary">
                <span className="font-medium">Email:</span> {currentUser.email || "—"}
              </p>
              {currentUser.fullName && (
                <p className="text-text-secondary mt-1">
                  <span className="font-medium">Name:</span> {currentUser.fullName}
                </p>
              )}
            </div>
          )}

          {/* Change password */}
          <div className="bg-background-card rounded-xl p-6 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Change password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Current password</label>
                <div className="relative">
                  <Input
                    type={showPasswords ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full p-3 rounded-lg bg-input-bg text-input-text border border-border-field"
                  />
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-text-secondary"
                    onClick={() => setShowPasswords(!showPasswords)}
                  >
                    {showPasswords ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">New password</label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters, uppercase, lowercase, number"
                  className="w-full p-3 rounded-lg bg-input-bg text-input-text border border-border-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Confirm new password</label>
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full p-3 rounded-lg bg-input-bg text-input-text border border-border-field"
                />
              </div>
              {passwordError && <p className="text-sm text-red-500">{passwordError}</p>}
              <Button
                type="submit"
                className="bg-accent hover:bg-accent text-text-main font-bold py-2 px-6 rounded-xl"
                disabled={passwordLoading}
              >
                {passwordLoading ? "Updating…" : "Update password"}
              </Button>
            </form>
          </div>

          {/* Change email */}
          <div className="bg-background-card rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Change email</h2>
            <form onSubmit={handleChangeEmail} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">New email address</label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  className="w-full p-3 rounded-lg bg-input-bg text-input-text border border-border-field"
                />
              </div>
              {emailError && <p className="text-sm text-red-500">{emailError}</p>}
              <Button
                type="submit"
                className="bg-accent hover:bg-accent text-text-main font-bold py-2 px-6 rounded-xl"
                disabled={emailLoading}
              >
                {emailLoading ? "Updating…" : "Update email"}
              </Button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Account;
